import { useState, useEffect } from 'react'
import { observer } from 'mobx-react-lite'
import classnames from 'classnames'
import { SpinnerLoader } from 'shared/ui'
import { IntegrationKey } from 'entities/Integrations'
import { useEntitiesContext } from 'entities/EntitiesExport'
import { EnumIntegrationTabs } from 'widgets/ContactsDetails'
import { Tabs } from 'widgets/ContactsDetails/ui/ContactsIntegrations/ui/shared/Tabs'
import { Properties } from 'widgets/ContactsDetails/ui/ContactsIntegrations/ui/shared/Entities/Properties'
import { Deals } from 'widgets/ContactsDetails/ui/ContactsIntegrations/ui/shared/Entities/Deals'
import { Accounts } from 'widgets/ContactsDetails/ui/ContactsIntegrations/ui/shared/Entities/Accounts'
import { useContactsIntegrationsContext } from 'widgets/ContactsDetails/ui/ContactsIntegrations/context/contactsIntegrationsContext'
import { ReconnectButton } from 'widgets/ContactsDetails/ui/ContactsIntegrations/ui/ReconnectButton/ReconnectButton'
import { Actions } from './Actions'
import styles from './styles.module.scss'

export const ActiveCampaignIntegration = observer(() => {
  const { contactsStore, integrationsStore } = useEntitiesContext()
  const store = useContactsIntegrationsContext()
  const [activeTab, setActiveTab] = useState<EnumIntegrationTabs>(store.integrationTab)
  const contact = contactsStore.getItem(store.selectedContact?.id)

  const handleActiveTab = (tab: EnumIntegrationTabs) => {
    setActiveTab(tab)
    store.handleIntegrationTab(tab)
  }

  useEffect(() => {
    integrationsStore.fetchIntegrations()
  }, [])

  useEffect(() => {
    if (!contact) return

    store.initContactIntegrationActiveCampaignInfo(contact.id)
  }, [contact])

  if (!contact) return null

  const integration = integrationsStore.getIntegration(IntegrationKey.activecampaign)

  if (!integration?.isConnected)
    return <ReconnectButton redirectKey={IntegrationKey.activecampaign} />

  const loading = store.loading || store.unlinking
  const info =
    integration.key === IntegrationKey.activecampaign
      ? store.getContactIntegrationActiveCampaignInfo(contact.id)
      : null

  if (loading)
    return (
      <div className={classnames(styles.loaderWrap)}>
        <SpinnerLoader ariaLabel={'ActiveCampaignIntegration'} size={24} />
      </div>
    )

  const ActiveTab = () => {
    if (!info) return null

    if (activeTab === EnumIntegrationTabs.Deals) {
      return (
        <Deals
          integrationLabel={'ActiveCampaign'}
          integrationKey={IntegrationKey.activecampaign}
          integrationIcon={'activecampaign'}
          deals={info.deals}
          contact={contact}
        />
      )
    }

    if (activeTab === EnumIntegrationTabs.Account) {
      return (
        <Accounts
          integrationLabel={'ActiveCampaign'}
          integrationKey={IntegrationKey.activecampaign}
          contact={contact}
          accounts={info.accounts}
        />
      )
    }

    if (activeTab === EnumIntegrationTabs.Contact) {
      return (
        <Properties
          integrationLabel={'ActiveCampaign'}
          integrationKey={IntegrationKey.activecampaign}
          property={info.properties}
          contact={contact}
          action={<Actions id={contact.integration_vendor_id} />}
        />
      )
    }

    return null
  }

  return (
    <div className={classnames(styles.wrap)}>
      <Tabs
        handleTab={handleActiveTab}
        activeTab={activeTab}
        items={[
          {
            key: EnumIntegrationTabs.Contact,
            title: 'Contact',
          },
          {
            key: EnumIntegrationTabs.Account,
            title: 'Account',
          },
          {
            key: EnumIntegrationTabs.Deals,
            title: 'Deals',
            enableCount: true,
            count: info?.deals?.length || 0,
          },
        ]}
      />
      <ActiveTab />
    </div>
  )
})
